export interface PipeJobNumberSource {
  jobNumber?: {
    numberOfJobs?: number;
    jobNumbers?: string[];
  };
}

export interface JobNumberOccurrence {
  pipeIndex: number;
  jobIndex: number;
  normalized: string;
  value: string;
}

export function normalizeJobNumber(value: unknown): string {
  return String(value ?? '').trim().toLowerCase();
}

export function getJobNumbersForPipe(pipe: PipeJobNumberSource): string[] {
  const jobNumbers = pipe?.jobNumber?.jobNumbers || [];
  const numberOfJobs = pipe?.jobNumber?.numberOfJobs ?? jobNumbers.length;
  return jobNumbers.slice(0, Math.max(0, numberOfJobs));
}

export function getJobNumberOccurrences(pipeSizes: PipeJobNumberSource[]): JobNumberOccurrence[] {
  const occurrences: JobNumberOccurrence[] = [];

  pipeSizes.forEach((pipe, pipeIndex) => {
    getJobNumbersForPipe(pipe).forEach((jobNumber, jobIndex) => {
      const normalized = normalizeJobNumber(jobNumber);
      if (!normalized) return;

      occurrences.push({
        pipeIndex,
        jobIndex,
        normalized,
        value: String(jobNumber).trim(),
      });
    });
  });

  return occurrences;
}

export function isJobNumberDuplicateAt(
  pipeSizes: PipeJobNumberSource[],
  pipeIndex: number,
  jobIndex: number
): boolean {
  const occurrences = getJobNumberOccurrences(pipeSizes);
  const target = occurrences.find(
    (occurrence) => occurrence.pipeIndex === pipeIndex && occurrence.jobIndex === jobIndex
  );
  if (!target) return false;

  const firstOccurrence = occurrences.find(
    (occurrence) => occurrence.normalized === target.normalized
  );
  if (!firstOccurrence) return false;

  return (
    firstOccurrence.pipeIndex !== pipeIndex || firstOccurrence.jobIndex !== jobIndex
  );
}

export function findCrossPipeDuplicateJobNumbers(
  pipeSizes: PipeJobNumberSource[]
): Array<{ value: string; pipeIndex: number; jobIndex: number; firstPipeIndex: number }> {
  const occurrences = getJobNumberOccurrences(pipeSizes);
  const firstByNormalized = new Map<string, JobNumberOccurrence>();
  const duplicates: Array<{ value: string; pipeIndex: number; jobIndex: number; firstPipeIndex: number }> = [];

  occurrences.forEach((occurrence) => {
    const firstOccurrence = firstByNormalized.get(occurrence.normalized);
    if (!firstOccurrence) {
      firstByNormalized.set(occurrence.normalized, occurrence);
      return;
    }

    duplicates.push({
      value: occurrence.value,
      pipeIndex: occurrence.pipeIndex,
      jobIndex: occurrence.jobIndex,
      firstPipeIndex: firstOccurrence.pipeIndex,
    });
  });

  return duplicates;
}

export function getCrossPipeDuplicateJobNumberErrors(
  pipeSizes: PipeJobNumberSource[]
): string[] {
  return findCrossPipeDuplicateJobNumbers(pipeSizes).map(
    ({ value, pipeIndex, firstPipeIndex }) =>
      `Pipe ${pipeIndex + 1}: Job number "${value}" is already used in Pipe ${firstPipeIndex + 1}`
  );
}
